import React, { useMemo, useState } from "react";
import { Slider, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { format } from "date-fns";

export interface DateRangeSliderProps {
  /** Currently selected start date  */
  startDate: Date | null;
  /** Currently selected end date  */
  endDate: Date | null;
  /** Minimum selectable date in date range  */
  minDate: Date | null;
  /** Maximum selectable date in date range  */
  maxDate: Date | null;
  setSliderStartDate: (value: Date) => void;
  setSliderEndDate: (value: Date) => void;
}

const MILLISECONDSINDAY = 1000 * 60 * 60 * 24;

const convertDateToNumber = (startDate: Date, date: Date): number => {
  const diffTime = Math.abs(date.getTime() - startDate.getTime());
  return Math.ceil(diffTime / MILLISECONDSINDAY);
};

const convertNumberToDate = (startDate: Date, value: number): Date => {
  return new Date(startDate.getTime() + value * MILLISECONDSINDAY);
};

/** Slider used for selecting dates between a given range */
export const DateRangeSlider = ({
  minDate,
  maxDate,
  startDate,
  endDate,
  setSliderStartDate,
  setSliderEndDate
}: DateRangeSliderProps): JSX.Element => {
  const [internalValue, setInternalValue] = useState<Number[]>([]);
  const {
    internalStartDate,
    internalEndDate,
    internalMaxDate
  } = useMemo(() => {
    let dates = {
      internalStartDate: 0,
      internalEndDate: 100,
      internalMaxDate: 100
    };

    if (minDate && maxDate) {
      dates.internalMaxDate = convertDateToNumber(minDate, maxDate);

      if (startDate) {
        dates.internalStartDate = convertDateToNumber(minDate, startDate);
      }

      if (endDate) {
        dates.internalEndDate = convertDateToNumber(minDate, endDate);
      } else {
        dates.internalEndDate = dates.internalMaxDate;
      }
    }

    return dates;
  }, [startDate, endDate, minDate, maxDate]);

  const handleChange = (value: number[]): void => {
    if (minDate) {
      // Only update startDate/endDate when the value of slider actually changes (to prevent re-rendering)
      if (value[0] !== internalValue[0] || value[1] !== internalValue[1]) {
        setSliderStartDate(convertNumberToDate(minDate, value[0]));
        setSliderEndDate(convertNumberToDate(minDate, value[1]));
        setInternalValue(value);
      }
    }
  };

  return (
    <>
      <StyledSlider
        disabled={!minDate || !maxDate}
        getAriaLabel={(index: number) =>
          `date slider ${index === 0 ? "min" : "max"}`
        }
        value={[internalStartDate, internalEndDate]}
        min={0}
        max={internalMaxDate}
        onChange={(_event: Event, value: number | number[]) =>
          handleChange(value as number[])
        }
        valueLabelDisplay="off"
      />
      <RangeLabelWrapper>
        <RangeLabel variant="caption">
          {minDate ? format(new Date(minDate), "MMM yy") : "--"}
        </RangeLabel>
        <RangeLabel variant="caption">
          {maxDate ? format(new Date(maxDate), "MMM yy") : "--"}
        </RangeLabel>
      </RangeLabelWrapper>
    </>
  );
};

const StyledSlider = styled(Slider)`
  &.MuiSlider-root {
    color: #7bc67e;
    .MuiSlider-markLabel {
      color: #00000061;
    }
    .MuiSlider-rail {
      color: #08044d;
      opacity: 1;
      border-radius: 0;
    }
    .MuiSlider-mark {
      color: #08044d;
      background-color: #08044d;
      border-radius: 0;
    }
  }
`;

const RangeLabelWrapper = styled("div")`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const RangeLabel = styled(Typography)`
  color: rgba(0, 0, 0, 0.38);
  font-family: "Roboto";
  font-style: normal;
  font-weight: 500;
  font-size: 10px;
  line-height: 14px;
  text-transform: uppercase;
`;
